import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  alpha,
  useTheme,
  Stack,
  Divider,
  Container,
  Skeleton,
} from "@mui/material";
import CustomSwitch from "../FormFields/CustomSwitch";
import CustomInput from "../FormFields/CustomInput";
import WeeklyPattern from "../Candidate/WeeklyPattern";
import CommonButton from "../CustomForm/CommonButton";
import { useQuery, useQueryClient } from "react-query";
import { QK } from "../../base/qk";
import {
  createOrUpdateWorkingPattern,
  getCandidateWorkingPattern,
} from "../../api/services/candidate";
import { useSnackbar } from "notistack";
import { Schedule as ScheduleIcon } from "@mui/icons-material";
import ConfirmModal from "../ConfirmModal/ConfirmModal";

const CandidateWorkingPattern = ({ clickedId }) => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [availableForWork, setAvailableForWork] = useState(false);
  const [workingPatternRequired, setWorkingPatternRequired] = useState(false);
  const [workOnBankHoliday, setWorkOnBankHoliday] = useState(false);
  const [availabilityNotes, setAvailabilityNotes] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [workingPatternData, setWorkingPatternData] = useState({});

  const { data: workingPattern, isLoading: isDataLoading } = useQuery(
    [QK.candidateWorkingPattern, clickedId],
    () => getCandidateWorkingPattern(clickedId),
    { enabled: !!clickedId }
  );

  useEffect(() => {
    if (workingPattern) {
      setAvailableForWork(workingPattern.is_available_for_work || false);
      setWorkingPatternRequired(
        workingPattern.working_pattern_required || false
      );
      setWorkOnBankHoliday(workingPattern.work_bank_holiday || false);
      setAvailabilityNotes(workingPattern.availability_notes || "");

      if (workingPattern.week1 && workingPattern.week2) {
        setWorkingPatternData({
          week1: {
            availability: workingPattern.week1.availability,
            times: workingPattern.week1.times,
          },
          week2: {
            availability: workingPattern.week2.availability,
            times: workingPattern.week2.times,
          },
        });
      }
    }
  }, [workingPattern]);

  const handleWeeklyPatternChange = (weekData) => {
    setWorkingPatternData((prevData) => ({
      ...prevData,
      [`week${weekData.weekNumber}`]: {
        availability: weekData.availability,
        times: weekData.times,
      },
    }));
  };

  const handleSubmit = () => {
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setShowConfirm(false);
    setIsLoading(true);

    const submissionData = {
      candidate: clickedId,
      is_available_for_work: availableForWork,
      working_pattern_required: workingPatternRequired,
      work_bank_holiday: workOnBankHoliday,
      availability_notes: availabilityNotes,
      ...(workingPatternRequired && {
        week1: workingPatternData.week1,
        week2: workingPatternData.week2,
      }),
    };
    const response = await createOrUpdateWorkingPattern(submissionData);
    if (response.status === 200 || response.status === 201) {
      enqueueSnackbar("Saved Working Pattern", { variant: "success" });
      queryClient.invalidateQueries([QK.candidateWorkingPattern]);
      queryClient.invalidateQueries([QK.employees]);
      queryClient.invalidateQueries([QK.unallocatedCandidates]);
      setIsLoading(false);
    } else {
      enqueueSnackbar("Something went wrong", { variant: "error" });
      setIsLoading(false);
    }
  };

  const renderSkeleton = () => (
    <Box sx={{ p: 2.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Skeleton variant="circular" width={32} height={32} />
        <Skeleton variant="text" width={200} height={28} />
        <Skeleton variant="text" width={150} height={20} />
      </Box>

      <Box sx={{ p: 2.5, bgcolor: "#FFFFFF", borderRadius: 2, border: "1px solid #E2E8F0" }}>
        <Stack spacing={2.5}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
            <Skeleton variant="rectangular" width={150} height={40} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" width={180} height={40} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" width={160} height={40} sx={{ borderRadius: 1 }} />
          </Box>

          <Divider />
          
          <Skeleton variant="rectangular" width={500} height={56} sx={{ borderRadius: 1 }} />

          <Divider>
            <Skeleton variant="text" width={120} height={20} />
          </Divider>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
            <Box sx={{ flex: "1 1 45%", minWidth: "300px" }}>
              <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
            </Box>
            <Box sx={{ flex: "1 1 45%", minWidth: "300px" }}>
              <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
            </Box>
          </Box>

          <Divider />

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Skeleton variant="rectangular" width={160} height={48} sx={{ borderRadius: 1 }} />
          </Box>
        </Stack>
      </Box>
    </Box>
  );

  if (isDataLoading) {
    return renderSkeleton();
  }

  return (
    <Container maxWidth="xl" sx={{ py: 0, px: 2 }}>
      <ConfirmModal
        show={showConfirm}
        handleClose={() => setShowConfirm(false)}
        handleConfirm={handleConfirm}
        isLoading={isLoading}
        title="Save Working Pattern"
        body="Are you sure you want to save this working pattern? This will update the candidate's availability schedule."
        confirmText="Save"
      />

      <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 3,
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ScheduleIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
          </Box>
          <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
            Working Pattern
          </Typography>
          <Typography variant="caption" color="#6B7280">
            Manage candidate availability and schedule
          </Typography>
        </Box>

        <Box
          sx={{
            p: 2.5,
            bgcolor: "#FFFFFF",
            borderRadius: 2,
            border: "1px solid #E2E8F0",
          }}
        >
          <Stack spacing={2.5}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 3,
                alignItems: "center",
              }}
            >
              <CustomSwitch
                label="Available For Work"
                checked={availableForWork}
                onChange={(e) => setAvailableForWork(e.target.checked)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#22C55E",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#22C55E",
                  },
                }}
              />
              <CustomSwitch
                label="Working Pattern Required"
                checked={workingPatternRequired}
                onChange={(e) => setWorkingPatternRequired(e.target.checked)}
              />
              <CustomSwitch
                label="Work on Bank Holiday"
                checked={workOnBankHoliday}
                onChange={(e) => setWorkOnBankHoliday(e.target.checked)}
              />
            </Box>

            <Divider />

            <Box sx={{ maxWidth: 500 }}>
              <CustomInput
                multiline
                label="Availability Notes"
                value={availabilityNotes}
                onChange={(e) => setAvailabilityNotes(e.target.value)}
                rows={2}
                fullWidth
                placeholder="Add any additional notes about availability..."
                size="small"
              />
            </Box>

            {workingPatternRequired && (
              <>
                <Divider>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color="#475467"
                    sx={{ px: 2 }}
                  >
                    Weekly Schedule
                  </Typography>
                </Divider>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2.5,
                  }}
                >
                  <Box sx={{ flex: "1 1 45%", minWidth: "300px" }}>
                    <WeeklyPattern
                      weekNumber={1}
                      initialAvailability={workingPatternData?.week1?.availability}
                      initialTimes={workingPatternData?.week1?.times}
                      onDataChange={handleWeeklyPatternChange}
                    />
                  </Box>
                  <Box sx={{ flex: "1 1 45%", minWidth: "300px" }}>
                    <WeeklyPattern
                      weekNumber={2}
                      initialAvailability={workingPatternData?.week2?.availability}
                      initialTimes={workingPatternData?.week2?.times}
                      onDataChange={handleWeeklyPatternChange}
                    />
                  </Box>
                </Box>
              </>
            )}

            <Divider />

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                pt: 1,
              }}
            >
              <CommonButton
                isLoading={isLoading}
                buttonText="Save Working Pattern"
                onClick={handleSubmit}
                sx={{
                  px: 4,
                  py: 1.2,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 600,
                  bgcolor: "#2469F0",
                  "&:hover": {
                    bgcolor: "#1A5BD4",
                    boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
                  },
                }}
              />
            </Box>
          </Stack>
        </Box>
      </form>
    </Container>
  );
};

export default CandidateWorkingPattern;